import api from './Api';

export interface UserProfile {
  id: number;
  username: string;
  email: string;
  fullName?: string;
  phone?: string;
  avatarUrl?: string;
  role: string;
}

export interface PersonalCompany {
  id: number;
  name: string;
  logoUrl?: string;
  description?: string;
  deposits?: any[];
}

export interface PersonalDeposit {
  id: number;
  name: string;
  location: string;
  description: string;
  company?: {
    id: number;
    name: string;
  };
}

export interface CompanyCategory {
  id: number;
  name: string;
  company: {
    id: number;
    name: string;
  };
}

export interface StatisticsReportPreference {
  enabled: boolean;
  frequency: 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'YEARLY';
  scope: 'COMPANIES' | 'DEPOSITS' | 'BOTH';
  recipientEmail: string;
  companyId?: number | null;
  depositId?: number | null;
  saleNotificationsEnabled: boolean;
  saleCompanyId?: number | null;
  saleDepositId?: number | null;
  saleRecipientEmail: string;
  nextSendAt?: string | null;
  lastSentAt?: string | null;
}

export const PersonalCabinetApi = {
  // Profile Management
  getProfile: () => api.get<UserProfile>(`/personal-cabinet/profile?_t=${Date.now()}`),
  
  updateProfile: (data: Partial<UserProfile>) => 
    api.put<UserProfile>('/personal-cabinet/profile', data),

  getStatisticsReportPreference: () =>
    api.get<StatisticsReportPreference>(`/personal-cabinet/statistics-report?_t=${Date.now()}`),

  updateStatisticsReportPreference: (data: StatisticsReportPreference) =>
    api.put<StatisticsReportPreference>('/personal-cabinet/statistics-report', data),

  sendStatisticsReportTest: (data: StatisticsReportPreference) =>
    api.post('/personal-cabinet/statistics-report/test', data),
  
  changePassword: (data: { currentPassword: string; newPassword: string }) =>
    api.put<string>('/personal-cabinet/profile/password', data),
  
  uploadAvatar: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post<string>('/personal-cabinet/profile/avatar', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  // Company Management - with cache busting
  getPersonalCompanies: () => 
    api.get<PersonalCompany[]>(`/personal-cabinet/companies?_t=${Date.now()}`),
  
  createPersonalCompany: (company: { name: string; description?: string }) => 
    api.post<PersonalCompany>('/personal-cabinet/companies', company),
  
  updatePersonalCompany: (companyId: number, company: { name: string; description?: string }) => 
    api.put<PersonalCompany>(`/personal-cabinet/companies/${companyId}`, company),
  
  deletePersonalCompany: (companyId: number) => 
    api.delete(`/personal-cabinet/companies/${companyId}`),
  
  uploadCompanyLogo: (companyId: number, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post<string>(`/personal-cabinet/companies/${companyId}/logo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  
  // Deposit Management - with cache busting
  getPersonalDeposits: () => 
    api.get<PersonalDeposit[]>(`/personal-cabinet/deposits?_t=${Date.now()}`),

  createPersonalDeposit: (deposit: { name: string; location?: string; description?: string }) => 
    api.post<PersonalDeposit>('/personal-cabinet/deposits', deposit),
  
  updatePersonalDeposit: (depositId: number, deposit: { name: string; location?: string; description?: string }) => 
    api.put<PersonalDeposit>(`/personal-cabinet/deposits/${depositId}`, deposit),
  
  deletePersonalDeposit: (depositId: number) => 
    api.delete(`/personal-cabinet/deposits/${depositId}`),

  // Deposit-Company Association
  attachDepositToCompany: (companyId: number, depositId: number) => 
    api.post(`/personal-cabinet/companies/${companyId}/deposits/${depositId}`),
  
  detachDepositFromCompany: (companyId: number, depositId: number) => 
    api.delete(`/personal-cabinet/companies/${companyId}/deposits/${depositId}`),

  // Category Management - with cache busting
  getCompanyCategories: (companyId: number) => 
    api.get<CompanyCategory[]>(`/personal-cabinet/companies/${companyId}/categories?_t=${Date.now()}`),
  
  createCompanyCategory: (companyId: number, category: { name: string }) => 
    api.post<CompanyCategory>(`/personal-cabinet/companies/${companyId}/categories`, category),
  
  updateCompanyCategory: (companyId: number, categoryId: number, category: { name: string }) => 
    api.put<CompanyCategory>(`/personal-cabinet/companies/${companyId}/categories/${categoryId}`, category),
  
  deleteCompanyCategory: (companyId: number, categoryId: number) => 
    api.delete(`/personal-cabinet/companies/${companyId}/categories/${categoryId}`),
};
